"use client"

import { useState } from "react"
import Link from "next/link"
import Image from "next/image"

const BRAND = {
  darkBlue: "#174A84",
  midBlue: "#2A70B0",
  lightBlue: "#3A6FCC",
  gold: "#F4B333",
  red: "#DF463B",
  grey: "#9B9EA0",
  silver: "#E1E3E4",
  white: "#FFFFFF",
  nearBlack: "#1A1A2E",
} as const

const PhoneIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
)

type SiteNavProps = {
  /** Route slug to mark as the active link (light blue). */
  active?: "home" | "about" | "conditions"
}

const NAV_ITEMS: { label: string; href: string; key: NonNullable<SiteNavProps["active"]> }[] = [
  { label: "Home", href: "/", key: "home" },
  { label: "About Us", href: "/about", key: "about" },
  { label: "Conditions", href: "/conditions", key: "conditions" },
]

/**
 * Site-wide top navigation. Same links, layout, and mobile drawer on every page.
 * The active page's link is highlighted in light blue.
 */
export function SiteNav({ active }: SiteNavProps) {
  const [open, setOpen] = useState(false)

  return (
    <header
      className="sticky top-0 z-50 bg-white border-b"
      style={{ borderColor: BRAND.silver }}
    >
      <nav
        className="mx-auto flex items-center justify-between h-16 px-5 sm:px-6"
        style={{ maxWidth: 1200 }}
      >
        <Link href="/" className="flex items-center shrink-0">
          <Image
            src="/images/ccm-colored-logo.png"
            alt="Critical Care MedFlight"
            width={160}
            height={52}
            className="h-9 w-auto object-contain"
          />
        </Link>

        {/* Desktop — links + Call Now, right-aligned */}
        <div className="hidden md:flex items-center gap-7 ml-auto">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.key}
              href={item.href}
              className="text-base font-medium transition-colors nav-link-hover"
              style={{ color: active === item.key ? BRAND.lightBlue : BRAND.nearBlack }}
            >
              {item.label}
            </Link>
          ))}
          <a
            href="tel:8004266557"
            className="flex items-center gap-2 font-heading font-medium text-base px-5 py-2.5 transition-all duration-300 ease-out hover:scale-[1.03] hover:shadow-md active:scale-[0.98] cta-lightblue"
            style={{ backgroundColor: BRAND.lightBlue, color: BRAND.white, borderRadius: 6 }}
          >
            <PhoneIcon />
            Call Now
          </a>
        </div>

        {/* Mobile — compact call button + hamburger */}
        <div className="flex md:hidden items-center gap-2">
          <a
            href="tel:8004266557"
            aria-label="Call (800) 426-6557"
            className="flex items-center justify-center w-10 h-10 transition-all duration-200 active:scale-[0.96]"
            style={{
              backgroundColor: BRAND.lightBlue,
              color: BRAND.white,
              borderRadius: 6,
            }}
          >
            <PhoneIcon className="w-4 h-4" />
          </a>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex items-center justify-center w-10 h-10 transition-colors"
            style={{
              color: BRAND.nearBlack,
              border: `1px solid ${BRAND.silver}`,
              borderRadius: 6,
              backgroundColor: BRAND.white,
            }}
          >
            {open ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div
          className="md:hidden border-t"
          style={{ borderColor: BRAND.silver, backgroundColor: BRAND.white }}
        >
          <div className="px-5 py-4 space-y-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.key}
                href={item.href}
                onClick={() => setOpen(false)}
                className="block font-heading font-medium text-base py-3 px-3 rounded-md transition-colors"
                style={{ color: active === item.key ? BRAND.lightBlue : BRAND.nearBlack }}
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-3 mt-2 space-y-2.5 border-t" style={{ borderColor: BRAND.silver }}>
              <a
                href="tel:8004266557"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-2 w-full font-heading font-medium text-base py-3 transition-all duration-300 ease-out cta-lightblue"
                style={{ backgroundColor: BRAND.lightBlue, color: BRAND.white, borderRadius: 6 }}
              >
                <PhoneIcon />
                Call (800) 426-6557
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
